//#region imports
import React, { useEffect, useMemo, useCallback, useState, useRef, forwardRef } from "react";

// virtualization
import { Virtuoso } from "react-virtuoso";

// Custom Virtuoso components to allow full content width (needed for sticky positioning)
const ListComponent = forwardRef(({ style, children, ...props }, ref) => (
  <div ref={ref} style={{ ...style, width: 'fit-content', minWidth: '100%' }} {...props}>
    {children}
  </div>
));

const ItemComponent = ({ children, ...props }) => (
  <div style={{ width: 'fit-content' }} {...props}>
    {children}
  </div>
);

// firebase
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { firestore } from "_utils/firebase-app";

// redux
import { useSelector, useDispatch } from "react-redux";
import { updateEmployeeList, selectTimelineEmployeeList } from "_redux/slices/timeline/employees";
import { setDashboardEmployeeList } from "_redux/slices/timeline/timeline-dashboard";
import { selectIata } from "_redux/slices/main-screen/user";
import { selectThirtyMinLayout, selectTouchScreen } from "_redux/slices/timeline/timeline-control";
import { selectSelectedTask, selectSelectedTasksList, selectSelectedMultiplePAXTask } from "_redux/slices/timeline/selected-task";
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import { selectFlightSlotLookup } from "_redux/slices/timeline/timeline-flight-list";

// time formatting
const timeFormatter = require("_utils/date-time-functions");

// components
import TimelineRow from "../timeline-components/timeline-row";

// timeline constants
import { TimelineColumnWidthRaw, EmployeeInformationColumnWidth, Timeline30MinLayoutColumnWidth, TimelineColumnHeightRaw } from "../timeline-constants";

//#endregion

// Shared Redux values context — subscribed once here, consumed by every JobList cell
export const JobListSharedContext = React.createContext(null);

const skeletonKeyframes = `
@keyframes timeline-skeleton-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.15; }
}
`;

const SkeletonRow = ({ index, columnCount }) => (
  <div style={{ display: 'flex', width: 'fit-content', animationDelay: `${index * 80}ms` }}>
    {/* employee info placeholder */}
    <div style={{
      width: EmployeeInformationColumnWidth,
      minWidth: EmployeeInformationColumnWidth,
      minHeight: TimelineColumnHeightRaw,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '0 12px',
      borderBottom: '1px solid #eee',
    }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e0e0e0', animation: 'timeline-skeleton-pulse 1.4s ease-in-out infinite', animationDelay: `${index * 80}ms`, flexShrink: 0 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ width: '70%', height: 10, borderRadius: 4, background: '#e0e0e0', animation: 'timeline-skeleton-pulse 1.4s ease-in-out infinite', animationDelay: `${index * 80 + 100}ms` }} />
        <div style={{ width: '45%', height: 8, borderRadius: 4, background: '#ebebeb', animation: 'timeline-skeleton-pulse 1.4s ease-in-out infinite', animationDelay: `${index * 80 + 200}ms` }} />
      </div>
    </div>
    {/* column placeholders */}
    {Array.from({ length: columnCount }).map((_, ci) => (
      <div key={ci} style={{
        width: Timeline30MinLayoutColumnWidth,
        minWidth: Timeline30MinLayoutColumnWidth,
        minHeight: TimelineColumnHeightRaw,
        borderRight: '1px solid #f0f0f0',
        borderBottom: '1px solid #eee',
      }} />
    ))}
  </div>
);

// Role priority for sorting (lower number = appears first) - moved outside component to avoid recreation
const rolePriority = {
  manager: 1,      // Supervisor
  allocator: 2,    // Allocator
  buggydriver: 3,  // Buggy Driver
  driver: 4,       // Driver
  cca: 5,          // CCA
  boarding: 6,     // Boarding Agent
  marshaller: 7,   // Marshaller
  desk: 8,         // Desk Host
};

const DUBRolePriority = {
  driver: 1,       // Driver
  marshaller: 2,   // Marshaller
  boarding: 3,     // Boarding Agent
  buggydriver: 4,  // Buggy Driver
  manager: 5,      // Supervisor
  allocator: 6,    // Allocator
  runner: 7,     // Runner
  cca: 8,          // CCA
  desk: 9,         // Desk Host
}

// lists each employee row in timeline
const EmployeeList = React.memo((props) => {

  //#region redux
  const dispatch = useDispatch();

  // user info
  const iata = useSelector(selectIata);
  const thirtyMinLayout = useSelector(selectThirtyMinLayout);

  // list + filter
  const timelineEmployeeList = useSelector(selectTimelineEmployeeList);                   // List of all employees for this timeline

  // Shared selectors — subscribed once, provided via context to all JobList/JobCard cells
  const selectedTask = useSelector(selectSelectedTask);
  const selectedTasksList = useSelector(selectSelectedTasksList);
  const usedTheme = useSelector(selectUsedTheme);
  const selectedMultiplePAXTask = useSelector(selectSelectedMultiplePAXTask);
  const touchScreen = useSelector(selectTouchScreen);
  const flightSlotLookup = useSelector(selectFlightSlotLookup);

  const sharedContextValue = useMemo(() => ({
    thirtyMinLayout, selectedTask, selectedTasksList, usedTheme, selectedMultiplePAXTask, touchScreen, flightSlotLookup, iata
  }), [thirtyMinLayout, selectedTask, selectedTasksList, usedTheme, selectedMultiplePAXTask, touchScreen, flightSlotLookup, iata]);
  //#endregion

  // Single timer shared by all rows (replaces per-row setInterval)
  const [timelineHour, setTimelineHour] = useState(() => {
    const now = new Date();
    const h = now.getHours();
    const m = now.getMinutes();
    if (thirtyMinLayout) {
      return `${String(h).padStart(2, "0")}:${m < 30 ? "00" : "30"}`;
    }
    return timeFormatter.formatHour(h);
  });

  useEffect(() => {
    const updateTimeline = () => {
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      if (thirtyMinLayout) {
        setTimelineHour(`${String(h).padStart(2, "0")}:${m < 30 ? "00" : "30"}`);
      } else {
        setTimelineHour(timeFormatter.formatHour(h));
      }
    };
    updateTimeline();
    const intervalId = setInterval(updateTimeline, 60000);
    return () => clearInterval(intervalId);
  }, [thirtyMinLayout]);

  // Horizontal virtualization: track which columns are in the viewport
  const COLUMN_BUFFER = 3;
  const [visibleStart, setVisibleStart] = useState(0);
  const [visibleEnd, setVisibleEnd] = useState(20);
  const rafId = useRef(null);

  useEffect(() => {
    const el = props?.scrollParentRef?.current;
    if (!el) return;

    const computeRange = () => {
      const scrollLeft = el.scrollLeft;
      const clientWidth = el.clientWidth;
      const colW = TimelineColumnWidthRaw;

      const start = Math.max(0, Math.floor(scrollLeft / colW) - COLUMN_BUFFER);
      const end = Math.ceil((scrollLeft + clientWidth) / colW) + COLUMN_BUFFER;

      setVisibleStart(prev => prev === start ? prev : start);
      setVisibleEnd(prev => prev === end ? prev : end);
    };

    const handleScroll = () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(computeRange);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    computeRange();

    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [props?.scrollParentRef?.current]);

  //#region useEffect

  // employee database listener - real-time updates from Firestore
  useEffect(() => {
    if (!iata) return;

    const employeeCollectionRef = collection(firestore, "employee");
    const employeeQuery = query(employeeCollectionRef, where("airport", "==", iata));

    const unsubscribe = onSnapshot(employeeQuery, (snapshot) => {
      const updatedEmployeeList = [];
      snapshot.forEach((doc) => {
        const employeeData = doc.data();
        if (employeeData.airport == iata) {
          updatedEmployeeList.push(employeeData);
        }
      });
      dispatch(updateEmployeeList(updatedEmployeeList));
      dispatch(setDashboardEmployeeList(updatedEmployeeList));
    });

    return () => unsubscribe();
  }, [iata, dispatch]);

  // Memoize sorted employees: first those in employeeIdsToListenTo (unread), then by role priority
  const employees = useMemo(() => {

    const priList = iata === "DUB"
      ? DUBRolePriority
      : rolePriority
    const idsToListenTo = props?.employeeIdsToListenTo ?? [];
    const inList = (emp) => Array.isArray(idsToListenTo) && idsToListenTo.includes(emp?.employeeId);
    return [...(timelineEmployeeList || [])].sort((a, b) => {
      const aInList = inList(a);
      const bInList = inList(b);
      if (aInList !== bInList) return aInList ? -1 : 1;
      const priorityA = priList[a?.role?.toLowerCase()] || 99;
      const priorityB = priList[b?.role?.toLowerCase()] || 99;
      if (priorityA !== priorityB) return priorityA - priorityB;
      // For drivers with same role priority: group by employeeEquipment so same equipment are next to each other
      const isDriver = (emp) => emp?.role?.toLowerCase() === "driver";
      if (isDriver(a) && isDriver(b)) {
        const eqA = (a?.employeeEquipment ?? "").toString();
        const eqB = (b?.employeeEquipment ?? "").toString();
        return eqA.localeCompare(eqB);
      }
      return 0;
    });
  }, [timelineEmployeeList, props?.employeeIdsToListenTo]);

  // Memoized row renderer for virtualized list
  const itemContent = useCallback((index) => {
    const employee = employees[index];
    return (
      <TimelineRow
        index={index}
        clickedColumn={props?.clickedColumn}
        setClickedColumn={props?.setClickedColumn}
        employee={employee}
        arrayLength={employees.length}
        timelineHour={timelineHour}
        visibleStart={visibleStart}
        visibleEnd={visibleEnd}
      />
    );
  }, [employees, props?.clickedColumn, props?.setClickedColumn, timelineHour, visibleStart, visibleEnd]);

  //#endregion

  // show skeleton loader while employees are loading
  if (!timelineEmployeeList || employees.length === 0) {
    const skeletonColumns = Math.ceil(window.innerWidth / TimelineColumnWidthRaw) + 2;
    return (
      <div style={{ width: 'fit-content', minWidth: '100%' }}>
        <style>{skeletonKeyframes}</style>
        {Array.from({ length: 12 }).map((_, i) => (
          <SkeletonRow key={i} index={i} columnCount={skeletonColumns} />
        ))}
      </div>
    );
  }

  return (
    <JobListSharedContext.Provider value={sharedContextValue}>
      <Virtuoso
        customScrollParent={props?.scrollParentRef?.current}
        totalCount={employees.length}
        itemContent={itemContent}
        itemKey={(index) => employees[index]?.employeeId}
        overscan={50}
        components={{
          List: ListComponent,
          Item: ItemComponent,
        }}
        style={{ width: 'fit-content', minWidth: '100%' }}
      />
    </JobListSharedContext.Provider>
  );


});

export default EmployeeList;